import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { CreditCard, CheckCircle2 } from "lucide-react";

export default function Billing() {
  const { data, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => (await api.get("/subscription")).data.data,
  });

  if (isLoading || !data) return <div className="space-y-4"><Skeleton className="h-32 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;

  const { subscription, current_plan, plans } = data;
  const usage = subscription?.usage || {};
  const limits = subscription?.limits || {};
  const trialEnd = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : null;

  const pct = (u, l) => (l && l > 0 ? Math.min(100, Math.round((u / l) * 100)) : 0);

  return (
    <div>
      <PageHeader title="Fatturazione" subtitle="Gestisci il tuo piano e monitora l'utilizzo" icon={CreditCard} />

      <Card className="mb-6 border-primary/30 bg-primary/5" data-testid="current-plan-card">
        <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-xl font-semibold">Piano {current_plan?.name || subscription?.plan_code}</h2>
              <Badge variant="secondary" className="capitalize">{subscription?.status === "trialing" ? "In prova" : subscription?.status}</Badge>
            </div>
            {daysLeft !== null && <p className="mt-1 text-sm text-muted-foreground">Periodo di prova: {daysLeft} giorni rimanenti</p>}
          </div>
          <Button className="rounded-lg" data-testid="upgrade-btn">Aggiorna piano</Button>
        </CardContent>
      </Card>

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        {[{ k: "users", l: "Utenti" }, { k: "channels", l: "Canali" }, { k: "messages", l: "Messaggi" }].map((m) => (
          <Card key={m.k}>
            <CardContent className="p-5">
              <div className="mb-2 flex items-center justify-between text-sm"><span className="text-muted-foreground">{m.l}</span><span className="font-medium">{usage[m.k] || 0} / {limits[m.k] === -1 ? "∞" : limits[m.k] || 0}</span></div>
              <Progress value={pct(usage[m.k] || 0, limits[m.k])} className="h-2" />
            </CardContent>
          </Card>
        ))}
      </div>

      <h3 className="mb-4 font-heading text-lg font-semibold">Piani disponibili</h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {plans?.map((p) => {
          const current = p.code === subscription?.plan_code;
          return (
            <Card key={p.code} className={current ? "border-primary" : ""} data-testid={`plan-${p.code}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">{p.name}{current && <Badge>Attuale</Badge>}</CardTitle>
                <p className="text-2xl font-semibold">{p.price === null ? "Custom" : p.price === 0 ? "Gratis" : `€${p.price}`}<span className="text-sm font-normal text-muted-foreground">{p.price ? "/mese" : ""}</span></p>
              </CardHeader>
              <CardContent>
                <p className="mb-3 text-xs text-muted-foreground">{p.description}</p>
                <ul className="space-y-1.5">
                  {p.features.map((f) => <li key={f} className="flex items-center gap-2 text-xs"><CheckCircle2 className="h-3.5 w-3.5 text-primary" /> {f}</li>)}
                </ul>
                <Button variant={current ? "outline" : "default"} className="mt-4 w-full rounded-lg" disabled={current}>{current ? "Piano attuale" : "Seleziona"}</Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <p className="mt-6 text-xs text-muted-foreground">I pagamenti reali saranno attivati in una fase successiva.</p>
    </div>
  );
}
